import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type KillGrowthClass, type StrengthGrowth } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatClassRange, formatGrowthPct, KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import { estimateHint } from '@/lib/strengthEstimate';
import { useAllianceLabel } from '@/lib/useCurrentAlliance';
import { downloadPoster, PosterCard, PosterFrame, PosterScaledPreview } from '@/components/Poster';
import { Loading, Panel, Segmented, Th, fmtM, playerLabel } from './shared';

// Plan 051 — monthly M-Sum growth per power class.

// Rising first — the point is that smaller players get their own stage.
const CLASS_ORDER: KillGrowthClass[] = ['rising', 'mid', 'top'];
const POSTER_TOP_N = 8;

export default function GrowthTab() {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const q = useQuery({
    queryKey: ['insights', 'strength-growth', month ?? 'default'],
    queryFn: () => api.getAdminStrengthGrowth(month),
  });
  const posterRef = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);

  if (q.isLoading || !q.data) return <Loading />;
  const data = q.data;

  const runExport = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      await downloadPoster(posterRef.current, `strength-growth-${data.month}.png`);
    } catch (err) {
      console.error(err);
      alert('Export failed. Check console.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          M-Sum growth in {data.monthLabel}, compared within power classes (thirds by
          M-Sum at the month's end). {data.players.length} players measured
          {data.notComparable > 0 && `, ${data.notComparable} not comparable`}.
        </p>
        {data.months.length > 0 && (
          <select
            value={data.month}
            onChange={(e) => setMonth(e.target.value)}
            className="input max-w-[180px]"
          >
            {data.months.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {data.players.length === 0 ? (
        <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          No player is measurable for this month.
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <PosterCard
            title="Poster"
            description="Class medians and the strongest growers per class."
            busy={busy}
            onExport={runExport}
          >
            <PosterScaledPreview>
              <div ref={posterRef}>
                <GrowthPoster data={data} />
              </div>
            </PosterScaledPreview>
          </PosterCard>
          <div className="space-y-4">
            <ClassTiles data={data} />
            <GrowthTable data={data} />
          </div>
        </div>
      )}
    </div>
  );
}

function ClassTiles({ data }: { data: StrengthGrowth }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {CLASS_ORDER.map((key) => {
        const c = data.classes.find((x) => x.key === key)!;
        const meta = KILL_GROWTH_CLASS_META[key];
        return (
          <div key={key} className={cn('rounded-lg border px-3 py-2', meta.chip)}>
            <div className="text-[10px] font-semibold uppercase tracking-wider">{meta.label}</div>
            {formatClassRange(c.minMSum, c.maxMSum) && (
              <div className="text-[10px] tabular-nums text-slate-400">
                M-Sum {formatClassRange(c.minMSum, c.maxMSum)}
              </div>
            )}
            <div className="text-xl font-semibold tabular-nums text-slate-100">
              {formatGrowthPct(c.medianPct)}
            </div>
            <div className="text-[11px] text-slate-400">
              median{c.medianDeltaM != null && ` +${fmtM(c.medianDeltaM)} M`} · {c.players}{' '}
              players
            </div>
            {c.best && (
              <div className="mt-1 text-[11px] text-slate-300">
                Best: {playerLabel(c.best.player)} {formatGrowthPct(c.best.pct)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type SortKey = 'pct' | 'delta' | 'streak';

function GrowthTable({ data }: { data: StrengthGrowth }) {
  const [filter, setFilter] = useState<KillGrowthClass | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('pct');
  const medianOf = new Map(data.classes.map((c) => [c.key, c.medianPct]));
  const rows = useMemo(() => {
    const list = data.players.filter((r) => filter === 'all' || r.class === filter);
    const key = (r: (typeof list)[number]) =>
      sort === 'pct' ? r.pct : sort === 'delta' ? r.deltaM : r.streak;
    return [...list].sort((a, b) => key(b) - key(a));
  }, [data.players, filter, sort]);

  return (
    <Panel
      title="Players"
      actions={
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All' },
            ...CLASS_ORDER.map((k) => ({ value: k, label: KILL_GROWTH_CLASS_META[k].label })),
          ]}
        />
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <Th left>#</Th>
              <Th left>Player</Th>
              <Th left>Class</Th>
              <Th>M-Sum</Th>
              <Th onClick={() => setSort('delta')} active={sort === 'delta'}>
                +M
              </Th>
              <Th onClick={() => setSort('pct')} active={sort === 'pct'}>
                %
              </Th>
              <Th onClick={() => setSort('streak')} active={sort === 'streak'}>
                Streak
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const meta = KILL_GROWTH_CLASS_META[r.class];
              const med = medianOf.get(r.class);
              return (
                <tr
                  key={r.player.id}
                  className="border-t border-slate-800/60"
                  title={estimateHint(r.fromEstimatedFrom, r.toEstimatedFrom)}
                >
                  <td className="px-2 py-1 tabular-nums text-slate-500">{i + 1}</td>
                  <td className="px-2 py-1">{playerLabel(r.player)}</td>
                  <td className="px-2 py-1 text-xs" style={{ color: meta.color }}>
                    {meta.label}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-400">
                    {r.fromEstimatedFrom && '≈'}
                    {fmtM(r.fromMSum, 1)} → {r.toEstimatedFrom && '≈'}
                    {fmtM(r.toMSum, 1)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">+{fmtM(r.deltaM)}</td>
                  <td
                    className={cn(
                      'px-2 py-1 text-right tabular-nums',
                      med != null && r.pct > med ? 'text-emerald-300' : 'text-slate-300',
                    )}
                  >
                    {formatGrowthPct(r.pct)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                    {r.streak > 0 ? `🔥 ${r.streak}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Green = above class median. Streak = months in a row above the class median.
      </p>
    </Panel>
  );
}

function GrowthPoster({ data }: { data: StrengthGrowth }) {
  const allianceLabel = useAllianceLabel();
  return (
    <PosterFrame
      accent="#22c55e"
      eyebrow="Alliance strength growth"
      title={`${allianceLabel} · Strength growth`}
      weekLabel={`${data.monthLabel} · ${data.players.length} players`}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, height: '100%' }}>
        {CLASS_ORDER.map((key) => {
          const c = data.classes.find((x) => x.key === key)!;
          const meta = KILL_GROWTH_CLASS_META[key];
          const top = data.players
            .filter((r) => r.class === key)
            .slice(0, POSTER_TOP_N);
          return (
            <div
              key={key}
              style={{
                borderRadius: 16,
                border: `2px solid ${meta.color}66`,
                background: `${meta.color}14`,
                padding: '16px 18px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: meta.color,
                }}
              >
                {meta.label}
              </div>
              {formatClassRange(c.minMSum, c.maxMSum) && (
                <div style={{ fontSize: 13, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
                  M-Sum {formatClassRange(c.minMSum, c.maxMSum)}
                </div>
              )}
              <div style={{ fontSize: 44, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                {formatGrowthPct(c.medianPct)}
              </div>
              <div style={{ fontSize: 14, color: '#94a3b8' }}>
                median{c.medianDeltaM != null && ` +${fmtM(c.medianDeltaM)} M`} · {c.players} players
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {top.map((r, i) => (
                  <div
                    key={r.player.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      fontSize: 17,
                      borderTop: i === 0 ? 'none' : '1px solid rgba(148,163,184,0.15)',
                      paddingTop: i === 0 ? 0 : 6,
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i + 1}. {playerLabel(r.player)}
                    </span>
                    <span style={{ fontWeight: 700, color: meta.color, fontVariantNumeric: 'tabular-nums' }}>
                      {formatGrowthPct(r.pct)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </PosterFrame>
  );
}
